import type { EntryGroupEntry } from "@/shared/cache";
import type { Entry } from "@/shared/types/entries";
import type {
	ApiErrorEnvelope,
	ApiResponse,
	RequestOptions,
} from "@/shared/types/request";

/**
 * Dependencies the action factories close over.
 *
 * The action queue is shared across the legacy DataProvider/EntriesContext
 * legacy entry store and the entries cache. Both stacks store entries in their own slice, so each action
 * mutates whichever caches the host happens to have wired up.
 *
 * Optional fields stay no-op when the host doesn't have that store:
 *  - `updateEntryGroups` / `refreshEntryGroups` are populated by hosts that read
 *    from the entry-group cache slice (`/entries` page).
 *  - `updateEntries` / `refreshRecords` are populated by hosts that read
 *    from the legacy entry stores (DataProvider, EntriesContext).
 *
 * Actions that fundamentally only make sense against one store (e.g.
 * `setStatus`, which is a v2-only PATCH) skip the optional helper when it's
 * undefined.
 */
export type ActionDeps = {
	makeApiRequest: (opts: RequestOptions) => Promise<ApiResponse>;
	updateEntries: (updater: (prev: Entry[]) => Entry[]) => () => void;
	refreshRecords: () => Promise<void>;
	updateEntryGroups?: (
		updater: (prev: EntryGroupEntry[]) => EntryGroupEntry[],
	) => () => void;
	refreshEntryGroups?: () => Promise<void>;
};

export function plural(n: number) {
	return n === 1 ? "entry" : "entries";
}

export function actingHeaders(timekeeperId?: string | null) {
	return timekeeperId
		? { "x-acting-for": timekeeperId, "x-act-as-prof-id": timekeeperId }
		: undefined;
}

/**
 * Structured error thrown by `apiCall` when a v2 response carries a full error
 * envelope (`code`, `type`, `conflicts`, etc.). v1 paths still throw plain
 * `Error` so callers that don't need to branch on `code` can keep working.
 */
export class ApiError extends Error {
	readonly envelope: ApiErrorEnvelope;
	constructor(envelope: ApiErrorEnvelope) {
		super(envelope.message);
		this.name = "ApiError";
		this.envelope = envelope;
	}
	get code() {
		return this.envelope.code;
	}
	get apiType() {
		return this.envelope.type;
	}
	get param() {
		return this.envelope.param;
	}
	get conflicts() {
		return this.envelope.conflicts;
	}
}

export async function apiCall(
	deps: ActionDeps,
	opts: RequestOptions,
): Promise<void> {
	const resp = await deps.makeApiRequest(opts);
	if (resp.status === "ERROR") {
		if (resp.apiError) throw new ApiError(resp.apiError);
		throw new Error(resp.error || "Request failed");
	}
}

/**
 * Refresh both the legacy entry store and the v2 cache when each is
 * configured. Used by every action's `onSuccess` / `onError` so the right
 * store rehydrates regardless of which page invoked the queue.
 */
export function refreshBoth(deps: ActionDeps): Promise<void> {
	const a = deps.refreshRecords();
	const b = deps.refreshEntryGroups?.() ?? Promise.resolve();
	// Both are fire-and-forget from the queue's POV — we don't need to surface
	// per-store failure since each store's own toast pipeline already does.
	return Promise.all([a, b]).then(() => undefined);
}

/**
 * Compose two rollback functions into one. Useful when an action runs
 * optimistic updates against both the legacy store and the v2 cache —
 * a single failure should unwind both writes.
 */
export function combineRollback(
	a: () => void,
	b: (() => void) | null,
): () => void {
	if (!b) return a;
	return () => {
		a();
		b();
	};
}
